
import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronRight, Users, Heart, Calendar, UserCheck, Ruler, Eye, Palette, Cigarette, Wine, Image, Target } from 'lucide-react';
import { ProfileFilters } from '@/hooks/useProfileFilters';
import { FilterCategory } from './HierarchicalFilterModal';

interface FilterCategoryListProps {
  filters: ProfileFilters;
  onUpdateFilter: (key: keyof ProfileFilters, value: any) => void;
  onCategoryClick: (category: FilterCategory) => void;
  availableInterests: string[];
}

const FilterCategoryList: React.FC<FilterCategoryListProps> = ({
  filters,
  onUpdateFilter,
  onCategoryClick,
  availableInterests
}) => {
  const getCategorySelectionCount = (filterKey: keyof ProfileFilters): number => {
    const value = filters[filterKey];
    if (Array.isArray(value)) {
      return value.length;
    }
    return 0;
  };

  const CategoryButton = ({ 
    category, 
    icon: Icon, 
    title, 
    filterKey 
  }: { 
    category: FilterCategory; 
    icon: any; 
    title: string; 
    filterKey: keyof ProfileFilters;
  }) => {
    const selectionCount = getCategorySelectionCount(filterKey);
    
    return (
      <Button
        variant="ghost"
        onClick={() => onCategoryClick(category)}
        className="w-full justify-between p-4 h-auto text-white/80 hover:text-white hover:bg-white/10"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span>{title}</span>
          {selectionCount > 0 && (
            <span className="bg-luxury-red text-white text-xs px-2 py-1 rounded-full">
              {selectionCount}
            </span>
          )}
        </div>
        <ChevronRight className="h-4 w-4" />
      </Button>
    );
  };

  return (
    <div className="space-y-6 p-4 overflow-y-auto h-full">
      {/* Informações Básicas */}
      <div className="space-y-3">
        <h3 className="font-medium text-white text-sm">Informações Básicas</h3>
        
        <CategoryButton
          category="gender"
          icon={Users}
          title="Gênero"
          filterKey="gender"
        />
        
        <CategoryButton
          category="orientation"
          icon={Heart}
          title="Orientação"
          filterKey="orientation"
        />

        {/* Idade - Dropdowns inline */}
        <div className="space-y-3 bg-white/5 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-white/80" />
            <span className="text-white/80">Idade</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-xs">Mín.</Label>
              <Select 
                value={filters.minAge?.toString() || '18'} 
                onValueChange={(value) => onUpdateFilter('minAge', parseInt(value))}
              >
                <SelectTrigger className="bg-black/20 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/20 z-50">
                  {Array.from({length: 48}, (_, i) => i + 18).map(age => (
                    <SelectItem key={age} value={age.toString()} className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                      {age}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs">Máx.</Label>
              <Select 
                value={filters.maxAge?.toString() || '65'} 
                onValueChange={(value) => onUpdateFilter('maxAge', parseInt(value))}
              >
                <SelectTrigger className="bg-black/20 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/20 z-50">
                  {Array.from({length: 48}, (_, i) => i + 18).map(age => (
                    <SelectItem key={age} value={age.toString()} className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                      {age}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Relacionamento */}
      <div className="space-y-3">
        <h3 className="font-medium text-white text-sm">Relacionamento</h3>
        
        <CategoryButton
          category="relationshipTypes"
          icon={Heart}
          title="Tipo de Relacionamento"
          filterKey="relationshipTypes"
        />
        
        <CategoryButton
          category="estadoCivil"
          icon={UserCheck}
          title="Estado Civil"
          filterKey="estadoCivil"
        />
      </div>

      {/* Características Físicas */}
      <div className="space-y-3">
        <h3 className="font-medium text-white text-sm">Físico</h3>
        
        <CategoryButton
          category="alturas"
          icon={Ruler}
          title="Altura"
          filterKey="alturas"
        />
        
        <CategoryButton
          category="tiposCorpo"
          icon={Users}
          title="Tipo Corporal"
          filterKey="tiposCorpo"
        />
        
        <CategoryButton
          category="coresOlhos"
          icon={Eye}
          title="Cor dos Olhos"
          filterKey="coresOlhos"
        />
        
        <CategoryButton
          category="coresCabelo"
          icon={Palette}
          title="Cor do Cabelo"
          filterKey="coresCabelo"
        />
      </div>

      {/* Estilo de Vida */}
      <div className="space-y-3">
        <h3 className="font-medium text-white text-sm">Estilo de Vida</h3>
        
        <CategoryButton
          category="fumante"
          icon={Cigarette}
          title="Fumante"
          filterKey="fumante"
        />
        
        <CategoryButton
          category="bebidas"
          icon={Wine}
          title="Bebidas"
          filterKey="bebidas"
        />
        
        <CategoryButton
          category="tatuagens"
          icon={Image}
          title="Tatuagens"
          filterKey="tatuagens"
        />
        
        <CategoryButton
          category="piercings"
          icon={Target}
          title="Piercings"
          filterKey="piercings"
        />
      </div>

      {/* Interesses */}
      <div className="space-y-3">
        <h3 className="font-medium text-white text-sm">Interesses</h3>
        
        <CategoryButton
          category="interests"
          icon={Heart}
          title="Interesses"
          filterKey="interests"
        />
      </div>
    </div>
  );
};

export default FilterCategoryList;
